<template>
  <div class="upd-writeoff-info">
    <q-linear-progress v-if="isFetching" indeterminate color="primary" class="upd-writeoff-info__loader" />

    <div class="upd-writeoff-info__badge">
      <span class="upd-writeoff-info__badge-icon">📋</span>
      <span>Списание: Через КС2В</span>
    </div>

    <q-form @submit="onSubmit" class="form">
      <div class="form__row form__row--info">
        <div class="form__hint form__hint--current">
          <template v-if="currentWriteoffCompanyName">
            <template v-if="currentProjectKindLabel">
              Сейчас в этом проекте для списания выбран вариант <strong>{{ currentProjectKindLabel }}</strong>, компания:
              <strong>{{ currentWriteoffCompanyName }}</strong>
            </template>
            <template v-else>
              Сейчас в этом проекте для списания выбрана компания: <strong>{{ currentWriteoffCompanyName }}</strong>
            </template>
          </template>
          <template v-else>
            В проекте компания для списания пока не выбрана.
          </template>
        </div>
      </div>

      <div v-if="writeoffCompanyChangeable" class="form__row">
        <label class="form__label">{{ writeoffCompanyLabel }}</label>
        <q-btn-toggle
          v-model="companyKind"
          spread
          no-caps
          unelevated
          rounded
          color="grey-2"
          text-color="grey-9"
          toggle-color="primary"
          toggle-text-color="white"
          class="upd-writeoff-info__kind-toggle"
          :options="[
            { label: 'Генподряд', value: 'GP' },
            { label: 'УК', value: 'UK' },
          ]"
        />
      </div>

      <div v-if="showGpBlock" class="form__row">
        <div class="form__field form__field--readonly">
          <label class="form__label">Генподряд</label>
          <div class="form__readonly-value">
            {{ gp_sbs_company_name || '—' }}
          </div>
        </div>
      </div>

      <div v-if="showUkBlock" class="form__row">
        <div v-if="canEdit" class="form__field">
          <label class="form__label">УК</label>
          <CustomInputDropdown
            option-value-key="sbs_company_id"
            option-label-key="naimenovanie"
            :value="ukSelectionName || ''"
            :options="sbsCompanyOptions"
            :loading="sbsCompanyLoading"
            :required="true"
            @change_input="sbsCompanyFilterFunc"
            @select_item="sbsCompanySelectItem"
          />
        </div>
        <div v-else class="form__field form__field--readonly">
          <label class="form__label">УК</label>
          <div class="form__readonly-value">
            {{ ukDisplayReadonly || '—' }}
          </div>
        </div>
      </div>

      <div class="form__row">
        <div class="form__field">
          <label class="form__label">Склад</label>
          <CustomSelect
            :options="skladOptions"
            option-label-key="naimenovanie"
            option-value-key="sbs_sklad_id"
            :value="formData.sbs_sklad_id"
            @change="value => formData.sbs_sklad_id = value"
            :required="true"
            style="width: 100%;"
          />
          <q-linear-progress v-if="loadingSklad" indeterminate color="primary" class="form__loader" />
        </div>
      </div>

      <div v-if="bottomHintText" class="form__row">
        <div class="form__hint" :class="{ 'form__hint--warn': bottomHintWarn }">
          {{ bottomHintText }}
        </div>
      </div>

      <CustomModalFooter :btn-loading="loading" submit-btn-text="Сохранить" />
    </q-form>
  </div>
</template>

<script>
import CustomSelect from "@/components/form/CustomSelect";
import CustomInputDropdown from "@/components/form/CustomInputDropdown";
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import { WRITEOFF_COMPANY_TYPE_CODE_GP, WRITEOFF_COMPANY_TYPE_CODE_UK } from "@/constants";
import { mapSBSCompany } from "@/views/Contractors/services";
import { projectsEntryAPI } from "@/views/ProjectsEntry/services/api";

export default {
  name: "upd-writeoff-info",
  components: { CustomSelect, CustomInputDropdown, CustomModalFooter },
  props: ["data"],
  data() {
    const body = this.data?.body ?? {};
    const canEdit = body.can_edit ?? true;
    const writeoffCompanyChangeable = body.writeoff_company_changeable === true;
    const ukIdRaw = body.uk_sbs_company_id;
    const writeoffCompanyIdRaw = body.sbs_writeoff_company_id;
    const ukIdStr = ukIdRaw != null ? String(ukIdRaw).trim() : "";
    const writeoffCompanyIdStr =
      writeoffCompanyIdRaw != null ? String(writeoffCompanyIdRaw).trim() : "";
    const ukIdFallback = ukIdStr || writeoffCompanyIdStr || "";
    const ukNameFallback =
      body.uk_sbs_company_name || body.sbs_writeoff_company_name || "";
    return {
      loading: false,
      isFetching: false,
      canEdit,
      writeoffCompanyChangeable,
      companyKind: writeoffCompanyChangeable ? "GP" : "UK",
      uk_sbs_company_id: body.uk_sbs_company_id ?? "",
      uk_sbs_company_name: body.uk_sbs_company_name || "",
      gp_sbs_company_id: body.gp_sbs_company_id ?? "",
      gp_sbs_company_name: body.gp_sbs_company_name || "",
      currentWriteoffCompanyName: body.sbs_writeoff_company_name || "",
      currentWriteoffCompanyId: body.sbs_writeoff_company_id ?? "",
      writeoffCompanyTypeCodeFromRead: body.writeoff_company_type_code ?? "",
      ukSelectionId: ukIdFallback,
      ukSelectionName: ukNameFallback,
      formData: {
        project_id: body.project_id ?? "",
        writeoff_type_code: "BY_SMETA",
        sbs_sklad_id: body.sbs_sklad_id ?? "",
      },
      skladOptions: [],
      loadingSklad: false,
      sbsCompanyOptions: [],
      sbsCompanyLoading: false,
      sbsCompanySearchTimeout: null,
    };
  },
  computed: {
    showUkBlock() {
      if (this.writeoffCompanyChangeable) {
        return this.companyKind === "UK";
      }
      return true;
    },
    showGpBlock() {
      return this.writeoffCompanyChangeable && this.companyKind === "GP";
    },
    writeoffCompanyLabel() {
      const code = String(this.writeoffCompanyTypeCodeFromRead || "").trim().toUpperCase();
      if (code === WRITEOFF_COMPANY_TYPE_CODE_GP) {
        return "Компания для списания Генподряд";
      }
      if (code === WRITEOFF_COMPANY_TYPE_CODE_UK) {
        return "Компания для списания УК";
      }
      return "Компания для списания";
    },
    currentProjectKindLabel() {
      const code = String(this.writeoffCompanyTypeCodeFromRead || "").trim().toUpperCase();
      if (code === WRITEOFF_COMPANY_TYPE_CODE_UK) {
        return "УК";
      }
      if (code === WRITEOFF_COMPANY_TYPE_CODE_GP) {
        return "Генподряд";
      }
      const cur = String(this.currentWriteoffCompanyId || "").trim();
      const uk = String(this.uk_sbs_company_id || "").trim();
      const gp = String(this.gp_sbs_company_id || "").trim();
      if (!cur) {
        return null;
      }
      const matchUk = Boolean(uk && cur === uk);
      const matchGp = Boolean(gp && cur === gp);
      if (matchUk && !matchGp) {
        return "УК";
      }
      if (matchGp && !matchUk) {
        return "Генподряд";
      }
      if (matchUk && matchGp) {
        return "УК и генподряд (одна компания)";
      }
      return null;
    },
    effectiveCompanyKind() {
      return this.writeoffCompanyChangeable ? this.companyKind : "UK";
    },
    ukDisplayReadonly() {
      return this.uk_sbs_company_name || this.ukSelectionName || "";
    },
    bottomHintText() {
      if (this.effectiveCompanyKind === "GP") {
        if (this.gp_sbs_company_name) {
          return `Будет проставлен генподряд: ${this.gp_sbs_company_name}`;
        }
        return "Назначьте руководителя проекта, чтобы определился генподряд для списания.";
      }
      const ukName = this.canEdit
        ? (this.ukSelectionName || this.uk_sbs_company_name)
        : this.uk_sbs_company_name;
      if (ukName) {
        return `Будет проставлен УК: ${ukName}`;
      }
      return "";
    },
    bottomHintWarn() {
      return (
        this.effectiveCompanyKind === "GP" && !this.gp_sbs_company_name
      );
    },
  },
  watch: {
    companyKind(kind) {
      if (kind === "UK" && this.canEdit) {
        const hasPick = this.ukSelectionId && this.ukSelectionName;
        if (!hasPick && (this.uk_sbs_company_id || this.uk_sbs_company_name)) {
          this.ukSelectionId = this.uk_sbs_company_id;
          this.ukSelectionName = this.uk_sbs_company_name;
        }
      }
    },
  },
  async mounted() {
    if (this.formData.project_id) {
      this.isFetching = true;
      await this.loadSkladOptions();
      this.isFetching = false;
    }
  },
  methods: {
    sbsCompanySelectItem(value, obj) {
      if (!obj || obj === null || obj === undefined) {
        this.ukSelectionId = "";
        this.ukSelectionName = "";
      } else {
        this.ukSelectionId = obj.sbs_company_id;
        this.ukSelectionName = obj.naimenovanie;
      }
    },
    async sbsCompanyFilterFunc(value) {
      if (!value) {
        this.ukSelectionId = "";
        this.ukSelectionName = "";
        this.sbsCompanyOptions = [];
        this.sbsCompanyLoading = false;
        return;
      }
      this.ukSelectionName = value;
      if (this.sbsCompanySearchTimeout) {
        clearTimeout(this.sbsCompanySearchTimeout);
      }
      if (value.length >= 3) {
        this.sbsCompanyLoading = true;
        this.sbsCompanySearchTimeout = setTimeout(async () => {
          const response = await mapSBSCompany({ search_text: value });
          this.sbsCompanyOptions = response || [];
          this.sbsCompanyLoading = false;
        }, 300);
      } else {
        this.sbsCompanyOptions = [];
        this.sbsCompanyLoading = false;
      }
    },
    async loadSkladOptions() {
      if (!this.formData.project_id) {
        this.skladOptions = [];
        return;
      }
      this.loadingSklad = true;
      const res = await projectsEntryAPI.getSkladBySectionForeman(this.formData.project_id);
      this.skladOptions = res?.data ?? [];
      this.loadingSklad = false;
    },
    resolveUkCompanyIdForSubmit() {
      const id = String(this.ukSelectionId || "").trim();
      if (id) {
        return id;
      }
      if (!this.canEdit) {
        return String(this.uk_sbs_company_id || "").trim();
      }
      const name = String(this.ukSelectionName || "").trim();
      if (!name) {
        return "";
      }
      const ukName = String(this.uk_sbs_company_name || "").trim();
      const ukId = String(this.uk_sbs_company_id || "").trim();
      if (ukId && name === ukName) {
        return ukId;
      }
      const curName = String(this.currentWriteoffCompanyName || "").trim();
      const curId = String(this.currentWriteoffCompanyId || "").trim();
      if (curId && name === curName) {
        return curId;
      }
      return "";
    },
    resolveSubmitCompany() {
      if (this.effectiveCompanyKind === "GP") {
        return {
          sbs_company_id: this.gp_sbs_company_id,
          writeoff_company_type_code: WRITEOFF_COMPANY_TYPE_CODE_GP,
        };
      }
      return {
        sbs_company_id: this.resolveUkCompanyIdForSubmit(),
        writeoff_company_type_code: WRITEOFF_COMPANY_TYPE_CODE_UK,
      };
    },
    async onSubmit() {
      if (!this.formData.sbs_sklad_id) {
        this.$q.notify({ type: "negative", message: "Необходимо указать склад.", position: "bottom-left" });
        return;
      }
      const resolved = this.resolveSubmitCompany();
      if (this.effectiveCompanyKind === "GP") {
        if (!this.gp_sbs_company_id) {
          this.$q.notify({
            type: "negative",
            message: "Нет генподряда: назначьте руководителя проекта или выберите списание через УК.",
            position: "bottom-left",
          });
          return;
        }
      } else if (!resolved.sbs_company_id) {
        this.$q.notify({ type: "negative", message: "Необходимо указать УК.", position: "bottom-left" });
        return;
      }
      this.loading = true;
      const body = {
        project_id: this.formData.project_id,
        writeoff_type_code: "BY_SMETA",
        sbs_sklad_id: this.formData.sbs_sklad_id,
        sbs_company_id: resolved.sbs_company_id,
        writeoff_company_type_code: resolved.writeoff_company_type_code,
      };
      const success = await this.data?.onSubmit(body);
      this.loading = false;
      if (success && this.data?.closeModal) {
        this.data.closeModal();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.upd-writeoff-info {
  min-width: 380px;
  max-width: 440px;
  padding: 4px 0;

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    border-radius: 0;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    margin-bottom: 20px;
    background: linear-gradient(135deg, #e8f4fd 0%, #f0f7fc 100%);
    border: 1px solid rgba(25, 118, 210, 0.12);
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.75);

    .upd-writeoff-info__badge-icon {
      font-size: 16px;
      opacity: 0.9;
    }
  }

  &__kind-toggle {
    width: 100%;

    /* Неактивная кнопка в фокусе без :focus-visible выглядела как выбранная (тот же primary) */
    :deep(.q-btn[aria-pressed="false"]) {
      &:focus:not(:focus-visible) .q-focus-helper {
        opacity: 0 !important;
      }
    }
  }
}

.form {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__row {
    width: 100%;

    &--info {
      margin-bottom: -6px;
    }
  }

  &__field {
    width: 100%;

    &--readonly {
      .form__readonly-value {
        padding: 10px 14px;
        background: #f5f5f5;
        border-radius: 8px;
        font-size: 14px;
        color: rgba(0, 0, 0, 0.85);
      }
    }
  }

  &__label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 6px;
    letter-spacing: 0.02em;
  }

  &__readonly-value {
    min-height: 40px;
    display: flex;
    align-items: center;
  }

  &__loader {
    margin-top: 6px;
    height: 2px;
  }

  &__hint {
    font-size: 12px;
    line-height: 1.45;
    color: rgba(0, 0, 0, 0.55);
    padding: 10px 12px;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 8px;

    &--current {
      background: rgba(25, 118, 210, 0.06);
      color: rgba(0, 0, 0, 0.7);
    }

    &--warn {
      color: #b71c1c;
      background: rgba(183, 28, 28, 0.06);
    }
  }
}
</style>
